// Development email service - simulates email sending without AWS SES
export interface EmailOptions {
  to: string;
  subject: string;
  htmlBody: string;
  textBody?: string;
  fromName?: string;
}

export interface EmailResult {
  success: boolean;
  error?: string;
  messageId?: string;
}

class DevEmailService {
  // Simulate email sending for development
  static async sendEmail(options: EmailOptions): Promise<EmailResult> {
    try {
      const sixDigitMatch = options.htmlBody.match(/(?<!\d)(\d{6})(?!\d)/);
      if (sixDigitMatch) {
        const code = sixDigitMatch[1];
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('dev_verification_code', code);
        }
      }

      console.log('📧 [DEV] Simulating email send...');
      console.log('📧 [DEV] To:', options.to);
      console.log('📧 [DEV] Subject:', options.subject);
      console.log('📧 [DEV] From:', options.fromName || 'Scubamail');

      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Generate a fake message ID
      const messageId = `dev-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      console.log('✅ [DEV] Email "sent" successfully');
      console.log('📧 [DEV] Message ID:', messageId);

      // In development, we'll also log the verification code to console
      const verificationCodeMatch = options.htmlBody.match(/(\d{6})/);
      if (verificationCodeMatch) {
        const verificationCode = verificationCodeMatch[1];
        console.log('🔑 [DEV] Verification Code:', verificationCode);
        console.log('📧 [DEV] Use this code to verify your email in the app');
        console.log('📧 [DEV] ⚠️  Note: This is a development simulation - no real email was sent');
        console.log('📧 [DEV] ⚠️  Check the console above for your verification code');
      }

      return {
        success: true,
        messageId
      };
    } catch (error) {
      console.error('❌ [DEV] Email simulation failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to simulate email'
      };
    }
  }

  // Send verification email
  static async sendVerificationEmail(
    userEmail: string,
    userName: string,
    verificationCode: string
  ): Promise<EmailResult> {
    // Use SystemEmailService to get the brand-consistent email templates for development testing too
    const SystemEmailService = (await import('./systemEmailService')).default;
    const htmlBody = SystemEmailService.getPasscodeEmail(userName, verificationCode, 'email_verification');

    const textBody = `
Verify Your Email - Scubamail

Hi \${userName},

Thank you for signing up with Scubamail! To complete your registration, please enter the following verification code in the app:

VERIFICATION CODE: \${verificationCode}

Important:
- This code will expire in 10 minutes
- Do not share this code with anyone
- If you didn't request this code, please ignore this email

If you didn't create an account with Scubamail, please ignore this email.

Best regards,
The Scubamail Team

© \${new Date().getFullYear()} Scubamail. All rights reserved.
    `;

    return await this.sendEmail({
      to: userEmail,
      subject: 'Verify Your Email - Scubamail',
      htmlBody,
      textBody,
      fromName: 'Scubamail'
    });
  }
}

export default DevEmailService;
